/**
 * nodeRegistry.tsx
 * 全ノード種別を一元管理するレジストリ
 * ノードの追加・取得・検索などの操作はこのファイルを経由する
 */
import type { ComponentType } from 'react';
import {
  type NodeKind,
  type NodeDataMap,
  type NodeDefinition,
} from './types';
import { definitions } from './nodes/definitions';
import { getNodeCategoryStyle, NODE_CATEGORIES } from './nodeCategories';

// ============ レジストリ定義 ============

/** 全ノード定義を種別ごとにマッピングしたオブジェクト */
export const nodeRegistry: { [K in NodeKind]: NodeDefinition<K> } = definitions;

// ============ ユーティリティ関数 ============

/** 登録されている全ノード種別を配列で取得 */
export const getNodeKinds = (): NodeKind[] =>
  Object.keys(nodeRegistry) as NodeKind[];

/**
 * 文字列がノード種別として有効かを判定する型ガード
 * Object.hasOwn を使用してプロトタイプチェーンを検索しない
 */
export const isNodeKind = (type: string | undefined): type is NodeKind =>
  type !== undefined && Object.hasOwn(nodeRegistry, type);

/**
 * パレットに表示するノード一覧を取得
 * showInPalette が true のノードのみ返す
 */
export const getPaletteItems = ({ includeMazeOnly = false }: { includeMazeOnly?: boolean } = {}) =>
  getNodeKinds()
    .filter((kind) => nodeRegistry[kind].showInPalette && (includeMazeOnly || !nodeRegistry[kind].mazeOnly))
    .map((kind) => ({
      kind,
      label: nodeRegistry[kind].label,
      category: NODE_CATEGORIES[kind],
      color: getNodeCategoryStyle(kind).palette,
      mazeOnly: nodeRegistry[kind].mazeOnly ?? false,
      imageKind: nodeRegistry[kind].paletteImageKind ?? kind,
    }));

/**
 * ReactFlow に渡すノードタイプマッピングを生成
 * { 'start': StartNodeView, 'end': EndNodeView, ... } の形式
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getNodeTypes = (): Record<string, ComponentType<any>> =>
  Object.fromEntries(
    getNodeKinds().map((kind) => [kind, nodeRegistry[kind].view])
  );

/** ミニマップ表示用の色を取得（未知のノードはグレー） */
export const getMiniMapColor = (nodeType: string | undefined): string =>
  isNodeKind(nodeType) ? getNodeCategoryStyle(nodeType).miniMap : '#888';

/** 指定種別のデフォルトデータを取得（新規ノード作成時に使用） */
export const getDefaultData = <K extends NodeKind>(kind: K): NodeDataMap[K] =>
  nodeRegistry[kind].defaultData;

/** 指定種別のヘルプ情報を取得（右パネル表示用） */
export const getHelp = <K extends NodeKind>(kind: K) =>
  nodeRegistry[kind].help;

/** 指定種別の設定ダイアログの中身を取得（設定項目が無いカードは undefined） */
export const getSettings = <K extends NodeKind>(kind: K) =>
  nodeRegistry[kind].settings;

/** 指定種別の実行関数を取得（フロー実行時に使用） */
export const getRunner = <K extends NodeKind>(kind: K) =>
  nodeRegistry[kind].run;
